import { whatsappMessagesEndpoint } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";
import { useInfiniteQuery } from "@tanstack/react-query";

// 1. THE ADAPTER (Frontend Logic for styling)
const adaptMessage = (msg: any) => ({
  ...msg,
  text: msg.body,
  isMine: msg.direction === "OUTBOUND",
  sent: msg.direction === "OUTBOUND",
  status: (msg.status || "sent").toLowerCase(),
  type: (msg.type || "text").toLowerCase(),
  mediaUrl: msg.mediaUrl || null,
  mediaMimeType: msg.mediaMimeType || null,
  mediaCaption: msg.mediaCaption || null,
  templateName: msg.templateName || null,
  errorCode: msg.errorCode || null,
  errorDesc: msg.errorDesc || null,
  senderName: msg.sender?.name || null,
  time: new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }),
});

type AdaptedMessage = ReturnType<typeof adaptMessage>;

/**
 * Messages fetched per request. The server caps a single call at 500 and
 * defaults to 100; 50 keeps the "open a chat" response small and lets the
 * panel page backwards through a long conversation as it is scrolled up.
 */
export const MESSAGE_PAGE_SIZE = 50;

/**
 * Pages are newest-first, each internally ascending — reverse the page order to
 * get one continuous oldest → newest list for the panel.
 *
 * Defined at module scope on purpose: React Query re-runs `select` whenever the
 * function identity changes, so an inline arrow would rebuild the whole flat
 * list on every render of every consumer.
 */
const selectFlatMessages = (data: { pages: AdaptedMessage[][] }) =>
  [...data.pages].reverse().flat();

/**
 * A lead's WhatsApp history, newest page first, paged BACKWARDS on demand.
 *
 * This was a plain `useQuery` with no params, so it only ever received the
 * server's default window (the most recent 100 messages) and had no way to ask
 * for anything older — scrolling up in a long conversation just hit a wall
 * mid-history with nothing to indicate more existed. The server has supported
 * the `beforeId` cursor all along; this hook now actually uses it.
 */
export default function useCurrentWhatsappMsgs(leadId: string | number) {
  const query = useInfiniteQuery({
    // Included leadId in queryKey to prevent cache mixing between leads
    queryKey: ["whatsapp-messages", leadId],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      if (!leadId) return [];

      const res = await axiosInstance.get(
        whatsappMessagesEndpoint.replace(":leadId", leadId.toString()),
        {
          params: {
            limit: MESSAGE_PAGE_SIZE,
            ...(pageParam ? { beforeId: pageParam } : {}),
          },
        }
      );

      // Your backend returns the array directly or inside a data object
      // This handles both cases: [ {..}, {..} ] OR { messages: [..] }
      const rawMessages: unknown[] = Array.isArray(res.data)
        ? res.data
        : res.data.messages || [];

      // Each page arrives ascending (oldest message of that page first).
      return rawMessages.map(adaptMessage);
    },

    initialPageParam: undefined as string | undefined,

    // "Next" here means OLDER: the cursor is the id of the earliest message we
    // hold, and the server returns messages strictly before it. A short page
    // means we've reached the start of the conversation.
    getNextPageParam: (lastPage: AdaptedMessage[]) => {
      if (!lastPage || lastPage.length < MESSAGE_PAGE_SIZE) return undefined;
      return lastPage[0]?.id as string | undefined;
    },

    select: selectFlatMessages,

    enabled: !!leadId, // Don't run if leadId is null
    staleTime: 1000 * 10, // Keep data fresh for 10 seconds
  });

  return {
    messages: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    /** Pull the previous page of older messages (scroll-up pagination). */
    fetchOlderMessages: query.fetchNextPage,
    hasOlderMessages: query.hasNextPage,
    isFetchingOlderMessages: query.isFetchingNextPage,
  };
}

/**
 * 🔄 Refetch specific lead messages
 */
export const refetchCurrentWhatsappMessage = async (
  leadId?: string | number
) => {
  if (leadId) {
    await queryClient.invalidateQueries({
      queryKey: ["whatsapp-messages", leadId],
    });
  } else {
    // Invalidate all if no leadId provided
    await queryClient.invalidateQueries({ queryKey: ["whatsapp-messages"] });
  }
};
